/**
 * Unified Time Formatting Utility
 * 
 * Compact, readable time formats without "about" prefix:
 * - Just now (< 1 min)
 * - 2m (< 60 min)
 * - 1h (< 24 hours, same day)
 * - Yesterday
 * - Mon, Tue, Wed (this week)
 * - Jan 5 (older, same year)
 * - Jan 5, 2025 (different year)
 * 
 * Handles both regular JS Date objects and Firestore Timestamps
 */

import { isToday, isYesterday, isThisYear, format } from 'date-fns';

/**
 * Safely convert any timestamp to a Date object
 * Handles: Date, Firestore Timestamp, ISO string, Unix timestamp
 * Returns null for invalid dates
 */
export function toDate(timestamp) {
  if (!timestamp) return null;
  
  let date;
  
  // Firestore Timestamp has .toDate() method
  if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  }
  // Already a Date
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // String or number
  else {
    date = new Date(timestamp);
  }
  
  // Validate the date is valid before returning
  if (!date || isNaN(date.getTime())) {
    return null;
  }
  
  return date;
}

/**
 * Format a timestamp in compact, readable format
 * Used for posts, notifications, onboarding cards, etc.
 */
export function formatTimeAgo(timestamp) {
  const date = toDate(timestamp);
  if (!date || isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Future dates (edge case)
  if (diffMs < 0) {
    return format(date, 'MMM d');
  }

  // Less than 1 minute
  if (diffMins < 1) {
    return 'Just now';
  }

  // Less than 60 minutes
  if (diffMins < 60) {
    return `${diffMins}m`;
  }

  // Today - show hours
  if (isToday(date)) {
    return `${diffHours}h`;
  }

  // Yesterday
  if (isYesterday(date)) {
    return 'Yesterday';
  }

  // Within last 6 days - show day name
  if (diffDays < 7) {
    return format(date, 'EEE'); // Mon, Tue, etc.
  }

  // This year - show month and day
  if (isThisYear(date)) {
    return format(date, 'MMM d'); // Jan 5
  }

  // Older - show full date
  return format(date, 'MMM d, yyyy'); // Jan 5, 2025
}

/**
 * Format with "ago" suffix for contexts where it reads better
 * e.g., "Added 2h ago" vs "Added 2h"
 */
export function formatTimeAgoWithSuffix(timestamp) {
  const formatted = formatTimeAgo(timestamp);
  
  if (!formatted) return '';
  
  // These don't need "ago" suffix
  if (['Just now', 'Yesterday'].includes(formatted) || 
      formatted.includes('/') || 
      formatted.includes(',') ||
      /^[A-Z][a-z]{2}$/.test(formatted) || // Day names like "Mon"
      /^[A-Z][a-z]{2} \d/.test(formatted)) { // "Jan 5" format
    return formatted;
  }
  
  // Add "ago" for relative times like "2m", "5h"
  return `${formatted} ago`;
}


/**
 * Convert 24-hour time string (HH:mm) to 12-hour AM/PM format.
 * e.g. "14:30" → "2:30 PM", "09:00" → "9:00 AM", "00:00" → "12:00 AM"
 */
export function formatTime12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time24;
  const ampm = h < 12 ? 'AM' : 'PM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * Generate time options in 15-minute intervals for dropdowns.
 * Each option has { value: "HH:mm", label: "h:mm AM/PM" }
 */
export function generateTimeOptions() {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      options.push({ value, label: formatTime12h(value) });
    }
  }
  return options;
}
